import React from "react";
import { useDispatch } from "react-redux";
import { useFlag } from '@innovid/feature-flags-react';
import { openModal } from "reducers/Modal";
import {
	hasPermission,
	isDynamic,
	isIncomplete,
	isSocialCreative,
	isCtvCreative,
	transcodingChecks,
	isSocialPreviewDisabled,
	noop
} from 'utils/common';
import { BasicAction, LinkAction } from "widgets/ActionMenu";
import {
	TIA_DOMAIN,
	MODAL_TYPES,
	CV,
	CREATIVE_VIEW
} from "utils/constants";
import { PreviewCreative, PreviewVersion } from "utils/Preview";
import{ isInvalidDVASTState, shouldDisplayAction } from 'utils/CreativeActions';
import { FEATURE_FLAGS } from 'featureFlag/featureFlag';

export function previewVersion(creative, versionName, forceNewPreview){
	window.open(PreviewVersion.private(creative, versionName, forceNewPreview).url, "_BLANK");
}

export function previewCreatives(ids){
	window.open(PreviewCreative.new.private(ids.join(',')), "_BLANK");
}

export function previewCreativesLegacy(ids){
	window.open(PreviewCreative.legacy.private(ids.join(',')), "_BLANK");
}

export function createMockup(id){
	window.open(`${TIA_DOMAIN}/mockup/${id}`, "_BLANK");
}

export function previewBulkCreativeVersions(ids){
	window.open(PreviewCreative.new.allVersions(ids.join(',')), "_BLANK");
}

export function previewBulkCreativeVersionsLegacy(ids){
	window.open(PreviewCreative.legacy.allVersions(ids.join(',')), "_BLANK");
}

export function BasicPreviewAction({ icon, name, pathFn = noop }, rowInfo){
	const forceNewPreview = useFlag(FEATURE_FLAGS.NEW_PREVIEW_LINKS);
	let path = pathFn(rowInfo, forceNewPreview)
		, className = "external-link-action preview-actions"
		, handleClick = () => window.open(path, "_BLANK");

	return (
		<BasicAction
			className={className}
			iconName={icon}
			name={name}
			handleClick={handleClick}
		/>
	);
}

export const QUICK_PREVIEW = {
	icon:"far fa-eye",
	name:"Quick Preview",
	bulkAction: false,
	component: ({ dependentValues }) => {
		let { libraryId, creativeId } = dependentValues
			, previewType = isSocialCreative(dependentValues) ? "social" : "basic";

		return(<LinkAction
			key="quick-creative-preview"
			name="Quick Preview"
			iconName="far fa-eye"
			useMenuItem
			className="action-link action-item"
			path={`/library/${libraryId}/modal/preview-creative/${previewType}/${creativeId}/`} />);
	},
};

export const PREVIEW_CREATIVE = {
	icon: "fas fa-image",
	name: "Preview Creative",
	pathFn: (dependentValues, forceNewPreview) => PreviewCreative.private({ ...dependentValues, id: dependentValues.creativeId }, forceNewPreview).url,
	shouldDisplay:({ dependentValues }) => shouldDisplayAction.creativePreview(dependentValues),
	component: ({ dependentValues }) => BasicPreviewAction({ ...PREVIEW_CREATIVE }, dependentValues),
};

export const CREATE_MOCKUP = {
	icon:"fas fa-window",
	name:"Create Mockup",
	bulkAction:true,
	pathFn:dependentValues=>`${TIA_DOMAIN}/mockup/${dependentValues.libraryId}`,
	shouldDisplay:({ dependentValues, user })=>{
		return !isSocialCreative(dependentValues) && hasPermission(user, 'Creative View', 'Create Mock-up Display');
	},
	component:({ dependentValues }) => BasicPreviewAction({ ...CREATE_MOCKUP }, dependentValues),
};

export const SHARE_CREATIVE = {
	icon:"fas fa-share-square",
	iconName:"fas fa-share-square",
	name:"Share Creative",
	shouldDisplay: ({ user, dependentValues }) => {
		return hasPermission(user, CV, CREATIVE_VIEW.SHARE_PREVIEW)
			&& !isCtvCreative(dependentValues)
			&& !isIncomplete(dependentValues)
			&& !transcodingChecks.transcoding(dependentValues)
			&& !isSocialPreviewDisabled(dependentValues)
		;
	},
	component: ({ dependentValues }) => {
		const dispatch = useDispatch();
		const { creativeId } = dependentValues || {};
		return (
			<BasicAction
				{...SHARE_CREATIVE}
				{...dependentValues}
				handleClick = {() => {
					dispatch(openModal(MODAL_TYPES.SHARE_CREATIVE_PREVIEWS, { creativeIds: [creativeId]}));
				}}
			/>
		);
	},
};

export const PREVIEW_VERSIONS = {
	icon: "fas fa-bolt",
	name: "Preview Versions",
	pathFn: (dependentValues, forceNewPreview) => PreviewCreative.allVersions({ ...dependentValues, id: dependentValues.creativeId }, forceNewPreview).url,
	shouldDisplay: ({ dependentValues })=>{
		let { numberOfVersions = 0 } = dependentValues || {};
		return isDynamic(dependentValues) && (numberOfVersions > 1) && !isInvalidDVASTState(dependentValues);
	},
	component: ({ dependentValues }) => BasicPreviewAction({ ...PREVIEW_VERSIONS  }, dependentValues),
};

export const SHARE_VERSIONS = {
	icon: "fas fa-share-square",
	iconName: "fas fa-share-square",
	name:"Share Versions",
	shouldDisplay: ({ dependentValues, user }) => {
		return SHARE_CREATIVE.shouldDisplay({ dependentValues, user }) && isDynamic(dependentValues);
	},
	component: ({ dependentValues }) => {
		const dispatch = useDispatch();
		const { creativeId } = dependentValues || {};
		return (
			<BasicAction
				{...SHARE_VERSIONS}
				{...dependentValues}
				handleClick = {() => {
					dispatch(openModal(MODAL_TYPES.SHARE_CREATIVE_PREVIEWS, {
						creativeIds: [creativeId],
						useVersionLinks: true,
					}));
				}}
			/>
		);
	},
};

export const PREVIEW_ACTION_LIST = [
	[QUICK_PREVIEW],
	[PREVIEW_CREATIVE, SHARE_CREATIVE],
	[PREVIEW_VERSIONS, SHARE_VERSIONS],
	[CREATE_MOCKUP]
];

export const PRIVATE ={
	QUICK_PREVIEW,
	CREATE_MOCKUP,
};
